/*
 * /ballistic-calculator — интерфейс. Физика — engine.js, патроны — loads.js.
 * Внутри всё в СИ (м, м/с, г, гПа, °C); единицы — только на входе и выходе.
 */
import { air, solve, pointBlank, stationFromSeaLevel, U, TABLES, fly, solveZero } from './engine.js';
import { LOADS } from './loads.js';

const CFG = window.__BC || { T: {}, cfg: { locale: 'en', slug: 'ballistic-calculator' } };
const T = CFG.T;
const SLUG = CFG.cfg.slug;
const LOCALE = CFG.cfg.locale;
const t = (k, r) => {
    let s = T[k] || k;
    if (r) for (const [a, b] of Object.entries(r)) s = s.split(':' + a).join(b);
    return s;
};
const $ = (id) => document.getElementById(id);
const nf = (d) => new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : LOCALE, { maximumFractionDigits: d, minimumFractionDigits: d });
const fmt = (v, d = 0) => (v == null || !isFinite(v) ? '—' : nf(d).format(v));

/* ── единицы ─────────────────────────────────────────────────────────────── */
const DIST = { metric: { k: 1, u: 'u_m' }, imperial: { k: U.M_PER_YD, u: 'u_yd' } };
const SMALL = { metric: { k: 0.01, u: 'u_cm' }, imperial: { k: U.M_PER_IN, u: 'u_in' } };
const SPEED = { metric: { k: 1, u: 'u_ms' }, imperial: { k: U.MPS_PER_FPS, u: 'u_fps' } };
const ENERGY = { metric: { k: 1, u: 'u_j' }, imperial: { k: U.J_PER_FTLB, u: 'u_ftlb' } };
const WIND = { metric: { k: 1, u: 'u_mps' }, imperial: { k: 0.44704, u: 'u_mph' } };
const ALT = { metric: { k: 1, u: 'u_m' }, imperial: { k: 0.3048, u: 'u_ft' } };
const PRESS = { hpa: 1, mmhg: 1.3332239, inhg: U.HPA_PER_INHG };
const toC = (v, units) => (units === 'imperial' ? (v - 32) * 5 / 9 : v);
const fromC = (v, units) => (units === 'imperial' ? v * 9 / 5 + 32 : v);

const IMPERIAL_DEFAULT = LOCALE === 'en';
const DEFAULT_LOAD = IMPERIAL_DEFAULT ? 'hd-6-5-creedmoor-143-gr-eld-x-precision-hunter' : 'sb-308-win-spce-180';

const S = {
    preset: DEFAULT_LOAD, massG: 11.66, v0: 765, bc: 0.176, model: 'G7', diamMm: 7.82, twistIn: null, lenMm: null,
    sightCm: IMPERIAL_DEFAULT ? 3.81 : 5, zeroM: IMPERIAL_DEFAULT ? 91.44 : 100, zeroUpCm: 0, click: IMPERIAL_DEFAULT ? 'moa:0.25' : 'mil:0.1',
    tempC: 15, hum: 50, press: 1013.25, pressKind: 'station', altM: 0,
    wind: 3, windClock: 3, angleDeg: 0, units: IMPERIAL_DEFAULT ? 'imperial' : 'metric',
    targetM: IMPERIAL_DEFAULT ? 274.32 : 200, stepM: IMPERIAL_DEFAULT ? 45.72 : 25, maxM: IMPERIAL_DEFAULT ? 548.64 : 400,
    zoneCm: IMPERIAL_DEFAULT ? 15.24 : 15, massU: IMPERIAL_DEFAULT ? 'gr' : 'g', v0U: IMPERIAL_DEFAULT ? 'fps' : 'ms',
    pressU: IMPERIAL_DEFAULT ? 'inhg' : (LOCALE === 'ru' || LOCALE === 'uk' ? 'mmhg' : 'hpa'),
};

/* ── сохранение ──────────────────────────────────────────────────────────── */
function load() {
    try {
        const saved = window.__toolSettings?.load(SLUG, 'state', null);
        if (saved && typeof saved === 'object') Object.assign(S, saved);
    } catch { /* приватный режим */ }
}
let saveTimer = 0;
function save() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => { try { window.__toolSettings?.save(SLUG, 'state', { ...S }); } catch { /* noop */ } }, 400);
}

/* ── чтение и запись полей ───────────────────────────────────────────────── */
const num = (el) => {
    const v = parseFloat(String(el.value).replace(',', '.').replace(/\s/g, ''));
    return isFinite(v) ? v : NaN;
};
const put = (el, v, d) => { el.value = isFinite(v) ? String(Math.round(v * 10 ** d) / 10 ** d) : ''; };

function paint() {
    const u = S.units;
    put($('bcMass'), S.massU === 'gr' ? S.massG / U.G_PER_GR : S.massG, S.massU === 'gr' ? 0 : 2);
    put($('bcV0'), S.v0U === 'fps' ? S.v0 / U.MPS_PER_FPS : S.v0, 0);
    put($('bcBc'), S.bc, 3);
    $('bcMassU').value = S.massU; $('bcV0U').value = S.v0U; $('bcPressU').value = S.pressU;
    put($('bcSight'), S.sightCm * 0.01 / SMALL[u].k, u === 'imperial' ? 2 : 1);
    put($('bcZero'), S.zeroM / DIST[u].k, 0);
    put($('bcZeroUp'), S.zeroUpCm * 0.01 / SMALL[u].k, 1);
    $('bcClick').value = S.click;
    put($('bcTemp'), fromC(S.tempC, u), 0);
    put($('bcHum'), S.hum, 0);
    put($('bcPress'), S.press / PRESS[S.pressU], S.pressU === 'inhg' ? 2 : (S.pressU === 'mmhg' ? 0 : 1));
    $('bcPressKind').value = S.pressKind;
    put($('bcAlt'), S.altM / ALT[u].k, 0);
    put($('bcWind'), S.wind / WIND[u].k, 1);
    $('bcWindDir').value = String(S.windClock);
    put($('bcAngle'), S.angleDeg, 0);
    $('bcUnits').value = u;
    put($('bcTarget'), S.targetM / DIST[u].k, 0);
    put($('bcStep'), S.stepM / DIST[u].k, 0);
    put($('bcMax'), S.maxM / DIST[u].k, 0);
    put($('bcZone'), S.zoneCm * 0.01 / SMALL[u].k, u === 'imperial' ? 1 : 0);
    put($('bcTwist'), S.twistIn, 1);
    put($('bcLen'), S.lenMm, 1);
    document.querySelectorAll('[data-model]').forEach((b) => {
        const on = b.dataset.model === S.model;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    $('bcPreset').value = LOADS.some((l) => l.id === S.preset) ? S.preset : 'custom';
    paintSource();
}

function readFields(changedId) {
    const u = S.units;
    const take = (id, fn) => { const v = num($(id)); if (isFinite(v)) fn(v); };
    if (changedId === 'bcMass') take('bcMass', (v) => { S.massG = S.massU === 'gr' ? v * U.G_PER_GR : v; });
    if (changedId === 'bcV0') take('bcV0', (v) => { S.v0 = S.v0U === 'fps' ? v * U.MPS_PER_FPS : v; });
    if (changedId === 'bcBc') take('bcBc', (v) => { S.bc = v; });
    if (changedId === 'bcSight') take('bcSight', (v) => { S.sightCm = v * SMALL[u].k * 100; });
    if (changedId === 'bcZero') take('bcZero', (v) => { S.zeroM = v * DIST[u].k; });
    if (changedId === 'bcZeroUp') take('bcZeroUp', (v) => { S.zeroUpCm = v * SMALL[u].k * 100; });
    if (changedId === 'bcTemp') take('bcTemp', (v) => { S.tempC = toC(v, u); });
    if (changedId === 'bcHum') take('bcHum', (v) => { S.hum = Math.max(0, Math.min(100, v)); });
    if (changedId === 'bcPress') take('bcPress', (v) => { S.press = v * PRESS[S.pressU]; });
    if (changedId === 'bcAlt') take('bcAlt', (v) => { S.altM = v * ALT[u].k; });
    if (changedId === 'bcWind') take('bcWind', (v) => { S.wind = Math.max(0, v * WIND[u].k); });
    if (changedId === 'bcAngle') take('bcAngle', (v) => { S.angleDeg = Math.max(-75, Math.min(75, v)); });
    if (changedId === 'bcTarget') take('bcTarget', (v) => { S.targetM = v * DIST[u].k; });
    if (changedId === 'bcStep') take('bcStep', (v) => { S.stepM = v * DIST[u].k; });
    if (changedId === 'bcMax') take('bcMax', (v) => { S.maxM = v * DIST[u].k; });
    if (changedId === 'bcZone') take('bcZone', (v) => { S.zoneCm = v * SMALL[u].k * 100; });
    if (changedId === 'bcTwist') { const v = num($('bcTwist')); S.twistIn = isFinite(v) && v > 0 ? v : null; }
    if (changedId === 'bcLen') { const v = num($('bcLen')); S.lenMm = isFinite(v) && v > 0 ? v : null; }
}

/* ── патроны ─────────────────────────────────────────────────────────────── */
function calLabel(cal) {
    if (cal === '12ga') return t('cal_group_12ga');
    if (cal === '20ga') return t('cal_group_20ga');
    return cal;
}

function fillPresets() {
    const sel = $('bcPreset');
    const groups = new Map();
    for (const L of LOADS) {
        if (!groups.has(L.cal)) groups.set(L.cal, []);
        groups.get(L.cal).push(L);
    }
    const cals = [...groups.keys()].sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
    for (const cal of cals) {
        const og = document.createElement('optgroup');
        og.label = calLabel(cal);
        for (const L of groups.get(cal)) {
            const o = document.createElement('option');
            o.value = L.id;
            const mass = S.massU === 'gr' ? `${L.gr} ${t('u_gr')}` : `${fmt(L.massG, 1)} ${t('u_g')}`;
            o.textContent = `${calLabel(cal)} — ${L.maker} ${L.line} · ${mass}`;
            og.appendChild(o);
        }
        sel.appendChild(og);
    }
}

function applyPreset(id) {
    const L = LOADS.find((x) => x.id === id);
    S.preset = L ? id : 'custom';
    if (!L) return;
    S.massG = L.massG; S.v0 = L.v0; S.diamMm = L.diamMm;
    S.model = L.model; S.bc = L.model === 'G7' ? L.bcG7 : L.bcG1;
}

function paintSource() {
    const L = LOADS.find((x) => x.id === S.preset);
    const p = $('bcSrc');
    if (!L) { p.hidden = true; return; }
    p.hidden = false;
    p.textContent = '';
    const parts = [];
    if (L.barrelMm) parts.push(t('hint_v0_barrel', { mm: fmt(L.barrelMm, 0) }));
    p.append(document.createTextNode(parts.join(' ') + (parts.length ? ' ' : '') + t('src_label') + ' '));
    const a = document.createElement('a');
    a.href = L.src; a.target = '_blank'; a.rel = 'noopener nofollow';
    a.textContent = t('src_link', { maker: L.maker });
    p.append(a);
}

/* ── расчёт ──────────────────────────────────────────────────────────────── */
function clickRad() {
    const [kind, val] = S.click.split(':');
    return parseFloat(val) * (kind === 'moa' ? U.RAD_PER_MOA : U.RAD_PER_MIL);
}

function atmoNow() {
    const station = S.pressKind === 'sea' ? stationFromSeaLevel(S.press, S.altM, S.tempC) : S.press;
    return air({ tempC: S.tempC, pressureHpa: station, humidity: S.hum / 100 });
}

function valid() {
    const bad = [];
    if (!(S.massG > 0.3 && S.massG < 200)) bad.push('bcMass');
    if (!(S.v0 > 80 && S.v0 < 1600)) bad.push('bcV0');
    if (!(S.bc > 0.02 && S.bc < 2)) bad.push('bcBc');
    if (!(S.zeroM >= 5 && S.zeroM <= 2000)) bad.push('bcZero');
    if (!(S.press > 300 && S.press < 1200)) bad.push('bcPress');
    if (!(S.stepM >= 1 && S.maxM > S.stepM)) bad.push('bcStep');
    return bad;
}

let lastResult = null;

function compute() {
    const bad = valid();
    document.querySelectorAll('.tc-in').forEach((el) => el.removeAttribute('aria-invalid'));
    bad.forEach((id) => $(id)?.setAttribute('aria-invalid', 'true'));
    if (bad.length) {
        $('bcAnswer').innerHTML = `<p class="tc-msg warn">${esc(t('err_input'))}</p>`;
        return null;
    }
    const atmo = atmoNow();
    const maxRange = Math.max(S.maxM, S.targetM);
    const base = {
        v0: S.v0, bc: S.bc, model: S.model, massG: S.massG, sightM: S.sightCm / 100, zeroM: S.zeroM,
        zeroUpM: S.zeroUpCm / 100, lookRad: S.angleDeg * Math.PI / 180, atmo, zeroAtmo: atmo,
        windSpeed: S.wind, windClock: S.windClock, step: S.stepM, maxRange,
        twistIn: S.twistIn, lengthIn: S.lenMm ? S.lenMm / 25.4 : null, diamIn: S.diamMm ? S.diamMm / 25.4 : null,
    };
    const res = solve(base);
    if (!res.ok || !res.rows.length) {
        $('bcAnswer').innerHTML = `<p class="tc-msg bad">${esc(t('err_zero', { d: fmtDist(S.zeroM) }))}</p>`;
        return null;
    }
    // строка точно на дистанции цели
    const one = solve({ ...base, step: S.targetM, maxRange: S.targetM });
    const tgt = one.ok && one.rows.length ? one.rows[one.rows.length - 1] : null;
    const pb = pointBlank({ v0: S.v0, bc: S.bc, model: S.model, sightM: S.sightCm / 100, atmo, maxRange: Math.max(600, maxRange) }, S.zoneCm / 100);
    lastResult = { res, tgt, pb, atmo };
    return lastResult;
}

/* ── вывод ───────────────────────────────────────────────────────────────── */
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const fmtDist = (m) => `${fmt(m / DIST[S.units].k, 0)} ${t(DIST[S.units].u)}`;
const fmtSmall = (m, d) => `${fmt(m / SMALL[S.units].k, d == null ? (S.units === 'imperial' ? 1 : 0) : d)}`;

const PR = new Intl.PluralRules(window.__intlLocale ? window.__intlLocale() : LOCALE);
/** «12 кликов» — слово по правилам множественного числа языка страницы. */
function clkWord(n) {
    const cat = PR.select(Math.abs(n));
    return T['clk_' + cat] || T.clk_other || '';
}
function clickWord(n) {
    return `${fmt(Math.abs(n), 0)} ${clkWord(n)}`;
}

function render() {
    const R = compute();
    if (!R) return;
    const { res, tgt, pb, atmo } = R;
    const u = S.units;
    const cr = clickRad();
    const [ckind] = S.click.split(':');
    const angU = ckind === 'moa' ? 'u_moa' : 'u_mil';
    const angOf = (rad) => (ckind === 'moa' ? rad / U.RAD_PER_MOA : rad * 1000);

    // ответ на дистанции цели
    if (tgt) {
        const holdUp = -tgt.angRad;                 // насколько поднять прицел
        const clicksUp = Math.round(holdUp / cr);
        const windCl = Math.round(-tgt.windRad / cr);
        const dropTxt = Math.abs(tgt.u) < 0.005 ? t('st_hold_none') : `${tgt.u > 0 ? '+' : '−'}${fmtSmall(Math.abs(tgt.u))} ${t(SMALL[u].u)}`;
        const dirUp = clicksUp === 0 ? '' : (clicksUp > 0 ? t('st_up') : t('st_down'));
        const dirW = windCl === 0 ? '' : (windCl > 0 ? t('st_right') : t('st_left'));
        const mpbr = pb && pb.maxRange ? t('mpbr_sub', {
            zone: `${fmtSmall(S.zoneCm / 100)} ${t(SMALL[u].u)}`,
            zero: fmtDist(pb.farZero || pb.peakAt),
            to: fmtDist(pb.maxRange),
        }) : t('mpbr_none');
        // Части ответа — заранее, без шаблона внутри шаблона: аудит непереведённого
        // текста (HardcodedCopyTest) читает вложенные кавычки как буквальный текст.
        const clicksVal = clicksUp === 0 ? '0' : esc(dirUp) + ' ' + fmt(Math.abs(clicksUp), 0);
        const windPart = windCl
            ? ' · ' + esc(t('st_wind')) + ' ' + esc(t(tgt.z < 0 ? 'drift_left' : 'drift_right')) + ': ' + esc(t('fix_word')) + ' ' + esc(dirW) + ' ' + esc(clickWord(windCl))
            : '';
        $('bcAnswer').innerHTML = `
            <div class="tc-answer">
                <span class="tc-answer-l">${esc(t('ans_title', { d: fmtDist(S.targetM) }))}</span>
                <span class="tc-answer-v">${clicksVal}<span class="u"> ${esc(clkWord(clicksUp))}</span></span>
                <p class="tc-answer-sub">${esc(dropTxt)} · ${fmt(Math.abs(angOf(holdUp)), ckind === 'moa' ? 1 : 2)} ${esc(t(angU))}${windPart}</p>
            </div>
            <div class="tc-stats" style="margin-top:10px">
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('st_drop'))}</div><div class="tc-stat-v">${esc(dropTxt)}</div><div class="tc-stat-s">${fmt(Math.abs(angOf(holdUp)), ckind === 'moa' ? 1 : 2)} ${esc(t(angU))}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('st_wind'))}</div><div class="tc-stat-v">${fmtSmall(Math.abs(tgt.z))} ${esc(t(SMALL[u].u))}</div><div class="tc-stat-s">${Math.abs(tgt.z) < 0.005 ? '—' : esc(t(tgt.z < 0 ? 'drift_left' : 'drift_right'))}${windCl ? ` · ${esc(t('fix_word'))} ${esc(dirW)} ${esc(clickWord(windCl))}` : ''}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('st_v'))}</div><div class="tc-stat-v">${fmt(tgt.v / SPEED[u].k, 0)}</div><div class="tc-stat-s">${esc(t(SPEED[u].u))} · M ${fmt(tgt.mach, 2)}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('st_e'))}</div><div class="tc-stat-v">${fmt(tgt.e / ENERGY[u].k, 0)}</div><div class="tc-stat-s">${esc(t(ENERGY[u].u))}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('st_t'))}</div><div class="tc-stat-v">${fmt(tgt.t, 2)}</div><div class="tc-stat-s">${esc(t('u_s'))}</div></div>
                <div class="tc-stat good"><div class="tc-stat-l">${esc(t('st_mpbr'))}</div><div class="tc-stat-v">${pb && pb.maxRange ? esc(fmtDist(pb.maxRange)) : '—'}</div><div class="tc-stat-s">${esc(mpbr)}</div></div>
            </div>`;
    }

    renderChart(res.rows, pb);
    renderTable(res.rows, cr, ckind, angU, angOf);

    const notes = [];
    if (res.transonicAt) notes.push(t('transonic_note', { d: fmtDist(res.transonicAt) }));
    if (res.subsonicAt) notes.push(t('subsonic_note', { d: fmtDist(res.subsonicAt) }));
    if (isFinite(res.sg)) notes.push(t('sg_note', { sg: fmt(res.sg, 2) }));
    $('bcNotes').textContent = notes.join(' ');
    renderRef();
    save();
}

function renderChart(rows, pb) {
    const W = 640, H = 220, padL = 44, padR = 12, padT = 14, padB = 28;
    const maxR = rows[rows.length - 1].range;
    const half = S.zoneCm / 200;
    const us = rows.map((r) => r.u);
    const lo = Math.min(-half * 1.4, ...us), hi = Math.max(half * 1.4, ...us);
    const X = (r) => padL + (r / maxR) * (W - padL - padR);
    const Y = (u) => padT + (hi - u) / (hi - lo) * (H - padT - padB);
    const pts = [[0, -S.sightCm / 100], ...rows.map((r) => [r.range, r.u])].map(([r, u]) => `${X(r).toFixed(1)},${Y(u).toFixed(1)}`).join(' ');
    const ticks = [];
    const tickStep = niceStep(maxR / DIST[S.units].k / 6) * DIST[S.units].k;
    for (let r = tickStep; r <= maxR + 1e-6; r += tickStep) {
        ticks.push(`<line class="ax" x1="${X(r)}" y1="${H - padB}" x2="${X(r)}" y2="${H - padB + 4}"/><text x="${X(r)}" y="${H - 8}" text-anchor="middle">${esc(fmt(r / DIST[S.units].k, 0))}</text>`);
    }
    const yTicks = [];
    const ys = niceStep((hi - lo) / SMALL[S.units].k / 4) * SMALL[S.units].k;
    for (let v = Math.ceil(lo / ys) * ys; v <= hi; v += ys) {
        yTicks.push(`<text x="${padL - 6}" y="${Y(v) + 4}" text-anchor="end">${esc(fmt(v / SMALL[S.units].k, 0))}</text>`);
    }
    const tgtX = S.targetM <= maxR ? `<line class="tgt" x1="${X(S.targetM)}" y1="${padT}" x2="${X(S.targetM)}" y2="${H - padB}"/>` : '';
    const svg = `<svg class="bc-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(t('chart_aria'))}">
        <rect class="zone" x="${padL}" y="${Y(half)}" width="${W - padL - padR}" height="${Y(-half) - Y(half)}"/>
        <line class="ax" x1="${padL}" y1="${H - padB}" x2="${W - padR}" y2="${H - padB}"/>
        <line class="los" x1="${padL}" y1="${Y(0)}" x2="${W - padR}" y2="${Y(0)}"/>
        ${tgtX}
        <polyline class="traj" points="${pts}"/>
        ${ticks.join('')}${yTicks.join('')}
        <text x="${W - padR}" y="${Y(0) - 5}" text-anchor="end">${esc(t('chart_los'))}</text>
        <text x="${padL + 4}" y="${Y(half) + 13}">${esc(t('chart_zone'))}</text>
    </svg>`;
    $('bcChartWrap').innerHTML = svg;
}

function niceStep(x) {
    const p = Math.pow(10, Math.floor(Math.log10(Math.max(x, 1e-9))));
    const m = x / p;
    return (m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10) * p;
}

function renderTable(rows, cr, ckind, angU, angOf) {
    const u = S.units;
    const head = `<thead><tr><th>${esc(t('th_range'))}, ${esc(t(DIST[u].u))}</th><th>${esc(t('th_drop'))}, ${esc(t(SMALL[u].u))}</th><th>${esc(t(angU))}</th><th>${esc(t('th_clicks'))}</th><th>${esc(t('th_wind'))}, ${esc(t(SMALL[u].u))}</th><th>${esc(t('th_wclicks'))}</th><th>${esc(t('th_v'))}, ${esc(t(SPEED[u].u))}</th><th>${esc(t('th_e'))}, ${esc(t(ENERGY[u].u))}</th><th>${esc(t('th_t'))}, ${esc(t('u_s'))}</th></tr></thead>`;
    const body = rows.map((r) => {
        const up = Math.round(-r.angRad / cr);
        const wc = Math.round(-r.windRad / cr);
        const cls = [Math.abs(r.range - S.targetM) < 0.5 ? 'bc-row-tgt' : '', r.mach < 1.2 ? 'bc-row-slow' : ''].join(' ').trim();
        return `<tr${cls ? ` class="${cls}"` : ''}><td class="num">${fmt(r.range / DIST[u].k, 0)}</td><td class="num">${r.u >= 0 ? '+' : '−'}${fmtSmall(Math.abs(r.u), 1)}</td><td class="num">${fmt(angOf(-r.angRad), ckind === 'moa' ? 1 : 2)}</td><td class="num">${up > 0 ? '↑' : (up < 0 ? '↓' : '')}${fmt(Math.abs(up), 0)}</td><td class="num">${r.z >= 0 ? '→' : '←'}${fmtSmall(Math.abs(r.z), 1)}</td><td class="num">${fmt(Math.abs(wc), 0)}</td><td class="num">${fmt(r.v / SPEED[u].k, 0)}</td><td class="num">${fmt(r.e / ENERGY[u].k, 0)}</td><td class="num">${fmt(r.t, 3)}</td></tr>`;
    }).join('');
    $('bcTable').innerHTML = head + `<tbody>${body}</tbody>`;
}

const REF_COND = {
    icao: () => air({ tempC: 15, pressureHpa: 1013.25, humidity: 0 }),
    asm: () => air({ tempC: 15, pressureHpa: 1000, humidity: 0.78 }),
    unknown: () => air({ tempC: 15, pressureHpa: 1013.25, humidity: 0 }),
};

function renderRef() {
    const L = LOADS.find((x) => x.id === S.preset);
    const panel = $('bcRefPanel');
    if (!L || !L.ref) { panel.hidden = true; return; }
    const ref = L.ref;
    const atmo = REF_COND[ref.cond]();
    const bc = ref.model === 'G7' ? L.bcG7 : L.bcG1;
    const sight = ref.sightM || 0.05;
    const bore = solveZero({ v0: L.v0, bc, table: TABLES[ref.model], sightM: sight, zeroM: ref.zeroM, atmo });
    const rows = fly({ v0: L.v0, bc, table: TABLES[ref.model], sightM: sight, boreRad: bore, lookRad: 0, atmo, wind: null, ranges: ref.dist });
    const yd = ref.yd;
    $('bcRefCond').textContent = t(ref.cond === 'icao' ? 'ref_cond_icao' : (ref.cond === 'asm' ? 'ref_cond_asm' : 'ref_cond_chart'));
    const head = `<thead><tr><th>${esc(t('ref_dist'))}</th><th>${esc(t('ref_v_maker'))}</th><th>${esc(t('ref_v_ours'))}</th><th>${esc(t('ref_u_maker'))}</th><th>${esc(t('ref_u_ours'))}</th></tr></thead>`;
    const body = rows.map((r, i) => {
        const dist = yd ? `${yd[i]} ${t('u_yd')}` : `${fmt(r.range, 0)} ${t('u_m')}`;
        const vMaker = yd ? `${fmt(ref.v[i] / U.MPS_PER_FPS, 0)} ${t('u_fps')}` : `${fmt(ref.v[i], 0)} ${t('u_ms')}`;
        const vOurs = yd ? `${fmt(r.v / U.MPS_PER_FPS, 0)} ${t('u_fps')}` : `${fmt(r.v, 0)} ${t('u_ms')}`;
        const uMaker = ref.u[i] == null ? '—' : (yd ? `${fmt(ref.u[i] / 2.54, 1)} ${t('u_in')}` : `${fmt(ref.u[i], 1)} ${t('u_cm')}`);
        const uOurs = ref.u[i] == null ? '—' : (yd ? `${fmt(r.u * 100 / 2.54, 1)} ${t('u_in')}` : `${fmt(r.u * 100, 1)} ${t('u_cm')}`);
        return `<tr><td>${esc(dist)}</td><td class="num">${esc(vMaker)}</td><td class="num">${esc(vOurs)}</td><td class="num">${esc(uMaker)}</td><td class="num">${esc(uOurs)}</td></tr>`;
    }).join('');
    $('bcRefTable').innerHTML = head + `<tbody>${body}</tbody>`;
    panel.hidden = false;
}

/* ── погода по месту ─────────────────────────────────────────────────────── */
async function fillHere() {
    const msg = $('bcHereMsg');
    msg.textContent = t('here_wait');
    let pos;
    try {
        pos = await new Promise((res, rej) => {
            if (!navigator.geolocation) { rej(new Error('nogeo')); return; }
            navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 });
        });
    } catch {
        msg.textContent = t('here_denied');
        return;
    }
    const lat = pos.coords.latitude.toFixed(2), lon = pos.coords.longitude.toFixed(2);
    try {
        const r = await fetch(`/api/weather/forecast?lat=${lat}&lon=${lon}`, { headers: { Accept: 'application/json' } });
        const j = await r.json();
        const now = Date.now() / 1000;
        const hours = Array.isArray(j.hours) ? j.hours : [];
        let h = null;
        for (const x of hours) {
            const ts = typeof x.t === 'number' ? x.t : Date.parse(x.t) / 1000;
            if (!h || Math.abs(ts - now) < Math.abs(h.ts - now)) h = { ...x, ts };
        }
        if (!h || !isFinite(h.temp) || !isFinite(h.press)) throw new Error('nohour');
        S.tempC = h.temp;
        if (isFinite(h.hum)) S.hum = h.hum;
        S.press = h.press;
        S.pressKind = 'sea';
        try { window.__stage?.('weather_here'); } catch (_) { /* телеметрия не мешает расчёту */ }
        if (pos.coords.altitude != null && isFinite(pos.coords.altitude)) S.altM = pos.coords.altitude;
        paint();
        render();
        msg.textContent = t('here_ok', {
            t: `${fmt(fromC(S.tempC, S.units), 0)}°`,
            p: `${fmt(S.press / PRESS[S.pressU], S.pressU === 'inhg' ? 2 : 0)} ${t('u_' + S.pressU)}`,
            h: fmt(S.hum, 0),
        });
    } catch (e) {
        window.__handleError?.(e, 'ballistic_weather', { say: t('here_fail') });
        msg.textContent = t('here_fail');
    }
}

/* ── PDF ─────────────────────────────────────────────────────────────────── */
async function pdf() {
    const el = $('bcResult');
    const L = LOADS.find((x) => x.id === S.preset);
    const head = $('bcPdfHead');
    const load = L ? `${L.cal} — ${L.maker} ${L.line}, ${fmt(L.massG, 1)} ${t('u_g')}` : `${fmt(S.massG, 1)} ${t('u_g')}`;
    // Строки шапки — заранее, без шаблона внутри шаблона (см. ответ выше).
    const pdfLoad = t('pdf_load', {
        load,
        v0: fmt(S.v0 / SPEED[S.units].k, 0) + ' ' + t(SPEED[S.units].u),
        bc: S.model + ' ' + fmt(S.bc, 3),
    });
    const pdfZero = t('pdf_zero', {
        zero: fmtDist(S.zeroM),
        sight: fmtSmall(S.sightCm / 100, 1) + ' ' + t(SMALL[S.units].u),
    });
    const pdfCond = t('pdf_cond', {
        t: fmt(fromC(S.tempC, S.units), 0) + '°',
        p: fmt(S.press / PRESS[S.pressU], S.pressU === 'inhg' ? 2 : 0) + ' ' + t('u_' + S.pressU),
        h: fmt(S.hum, 0),
    });
    head.innerHTML = `<b>${esc(t('pdf_title'))}</b><br>${esc(pdfLoad)}<br>${esc(pdfZero)}<br>${esc(pdfCond)}`;
    el.classList.add('bc-printing');
    try {
        const r = await window.__generatePDF?.({ element: el, filename: t('pdf_file') + '.pdf', title: t('pdf_title') });
        if (r && r.ok === false) window.__toast?.(t('pdf_fail'), 'error');
    } catch (e) {
        window.__handleError?.(e, 'ballistic_pdf', { say: t('pdf_fail') });
    } finally {
        el.classList.remove('bc-printing');
    }
}

/* ── запуск ──────────────────────────────────────────────────────────────── */
function fillWindDir() {
    const sel = $('bcWindDir');
    const names = { 12: 'wind_head', 3: 'wind_right', 6: 'wind_tail', 9: 'wind_left' };
    for (let h = 1; h <= 12; h++) {
        const o = document.createElement('option');
        o.value = String(h);
        o.textContent = t('clock_n', { n: String(h) }) + (names[h] ? ` — ${t(names[h])}` : '');
        sel.appendChild(o);
    }
}

let completed = false;
function userChanged() {
    if (!completed) { completed = true; window.__aeToolComplete?.({ kind: 'table' }); }
}

let renderTimer = 0;
const later = () => { clearTimeout(renderTimer); renderTimer = setTimeout(render, 120); };

function boot() {
    load();
    fillPresets();
    fillWindDir();
    if (!LOADS.some((l) => l.id === S.preset)) S.preset = 'custom';
    paint();
    const ids = ['bcMass', 'bcV0', 'bcBc', 'bcSight', 'bcZero', 'bcZeroUp', 'bcTemp', 'bcHum', 'bcPress', 'bcAlt', 'bcWind', 'bcAngle', 'bcTarget', 'bcStep', 'bcMax', 'bcZone', 'bcTwist', 'bcLen'];
    for (const id of ids) {
        $(id).addEventListener('input', () => { readFields(id); later(); userChanged(); });
        $(id).addEventListener('change', () => { paint(); render(); });
    }
    $('bcPreset').addEventListener('change', (e) => { applyPreset(e.target.value); paint(); render(); userChanged(); });
    $('bcMassU').addEventListener('change', (e) => { S.massU = e.target.value; paint(); });
    $('bcV0U').addEventListener('change', (e) => { S.v0U = e.target.value; paint(); });
    $('bcPressU').addEventListener('change', (e) => { S.pressU = e.target.value; paint(); save(); });
    $('bcPressKind').addEventListener('change', (e) => { S.pressKind = e.target.value; render(); userChanged(); });
    $('bcClick').addEventListener('change', (e) => { S.click = e.target.value; render(); userChanged(); });
    $('bcWindDir').addEventListener('change', (e) => { S.windClock = parseInt(e.target.value, 10) || 3; render(); userChanged(); });
    $('bcUnits').addEventListener('change', (e) => { S.units = e.target.value; paint(); render(); });
    document.querySelectorAll('[data-model]').forEach((b) => b.addEventListener('click', () => {
        const L = LOADS.find((x) => x.id === S.preset);
        S.model = b.dataset.model;
        if (L) { const v = S.model === 'G7' ? L.bcG7 : L.bcG1; if (v) S.bc = v; }
        paint(); render(); userChanged();
    }));
    $('bcHere').addEventListener('click', () => { fillHere(); userChanged(); });
    $('bcPdf').addEventListener('click', pdf);
    render();
}

try {
    boot();
} catch (e) {
    window.__handleError?.(e, 'ballistic_boot', { say: t('load_error') });
}
